import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomMutation, cacheKeys } from '../../graphql';
import { useIsInImpersonateUser } from '../../auth';

export const useAdvanceOnboarding = () => {
  const queryClient = useQueryClient();

  const { mutate: advanceOnboarding } = useCustomMutation({
    query: ADVANCE_ONBOARDING_MUTATION,
    options: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: cacheKeys('issue').all,
        });
      },
    },
  });

  return { advanceOnboarding };
};

/**
 * Hook that automatically advances onboarding when an onboarding issue is viewed
 */
export const useOnboardingIssueView = (issue: any) => {
  const { advanceOnboarding } = useAdvanceOnboarding();
  const isInImpersonateUser = useIsInImpersonateUser();
  const hasAdvanced = useRef(false);

  useEffect(() => {
    // Skip onboarding advancement when impersonating a user
    if (isInImpersonateUser) return;

    // Only advance once per mount, and only for onboarding issues with a next issue
    if (issue && issue.onboarding?.nextIssue && !hasAdvanced.current) {
      hasAdvanced.current = true;
      advanceOnboarding({ issueId: issue.id });
    }
  }, [
    issue?.id,
    issue?.onboarding?.nextIssue,
    advanceOnboarding,
    isInImpersonateUser,
  ]);
};

const ADVANCE_ONBOARDING_MUTATION = `
  mutation advanceOnboarding($issueId: ID!) {
    issue {
      advanceOnboarding(issueId: $issueId) {
        success
        message
      }
    }
  }
`;
